import { Icon } from "@iconify/react";
import { useRef } from "react";
import { tinaField, useTina } from "tinacms/dist/react";
import ButtonPrimary from "../ui-elements/ButtonPrimary";
import content from "../../../content/pages/massage-services.json";
import { PageDocument } from "../../../tina/__generated__/types";
import { PageSeo } from "../../navigation/PageSeo";
import "./MassageServices.css";

const initialData = { page: content };
const variables = { relativePath: "massage-services.json" };

// Renders "\n" in editable text as <br /> so multi-line headings stay editable as one field.
const withLineBreaks = (value: string | null | undefined) =>
  (value ?? "").split("\n").map((line, index, lines) => (
    <span key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ));

export const MassageServices = () => {
  const { data } = useTina({ query: PageDocument, variables, data: initialData });
  const { hero, method, options, story, benefitCards, spineGuide, feature } = data.page;
  const spineImageDialog = useRef<HTMLDialogElement>(null);

  return (
    <main className="kma-page">
      <PageSeo pathname="/massage-services" seo={data.page.seo} />
      <section className="kma-hero page-hero-dots">
        <div className="kma-hero-copy">
          <p className="kma-eyebrow" data-tina-field={tinaField(hero, "eyebrow")}>
            {hero.eyebrow}
          </p>
          <h1 data-tina-field={tinaField(hero, "title")}>
            {hero.title}
            <br />
            <span>{hero.titleHighlight}</span>
          </h1>
          <p className="kma-intro" data-tina-field={tinaField(hero, "intro")}>
            {hero.intro}
          </p>
          <ButtonPrimary url={hero.ctaHref ?? "/booking"} label={hero.ctaLabel ?? ""} size="md" />
        </div>
        <div className="kma-hero-image">
          <img
            src={hero.imageSrc ?? undefined}
            alt={hero.imageAlt ?? ""}
            data-tina-field={tinaField(hero, "imageSrc")}
          />
          <div className="kma-image-note">
            <Icon icon="tabler:leaf" />
            <span data-tina-field={tinaField(hero, "imageNote")}>{hero.imageNote}</span>
          </div>
        </div>
      </section>

      <section className="kma-method" aria-labelledby="kma-method-title">
        <div className="kma-method-copy">
          <p className="kma-eyebrow" data-tina-field={tinaField(method, "eyebrow")}>
            {method.eyebrow}
          </p>
          <h2 id="kma-method-title" data-tina-field={tinaField(method, "title")}>
            {method.title}
          </h2>
          {(method.paragraphs ?? []).map((paragraph, index) => (
            <p key={index} data-tina-field={tinaField(method, "paragraphs", index)}>
              {paragraph}
            </p>
          ))}
          <p className="kma-method-mantra" data-tina-field={tinaField(method, "mantra")}>
            {method.mantra}
          </p>
        </div>
        <div className="kma-focus-areas">
          <h3 data-tina-field={tinaField(method, "focusTitle")}>{method.focusTitle}</h3>
          <ul>
            {(method.focusAreas ?? []).map((area, index) => (
              <li key={index} data-tina-field={tinaField(method, "focusAreas", index)}>
                {area}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="kma-service-options" aria-labelledby="kma-service-options-title">
        <div className="kma-service-options-heading">
          <p className="kma-eyebrow" data-tina-field={tinaField(options, "eyebrow")}>
            {options.eyebrow}
          </p>
          <h2 id="kma-service-options-title" data-tina-field={tinaField(options, "title")}>
            {options.title}
          </h2>
          <p data-tina-field={tinaField(options, "description")}>{options.description}</p>
        </div>
        <div className="kma-service-options-grid">
          {(options.items ?? []).map((item, index) => (
            <article
              key={index}
              className={`kma-service-option${item.featured ? " kma-service-option-combo" : ""}`}
              data-tina-field={tinaField(item)}
            >
              <p className="kma-service-duration">{item.duration}</p>
              <h3>{item.name}</h3>
              {item.description && <p>{item.description}</p>}
              {(item.steps ?? []).length > 0 && (
                <ol>
                  {(item.steps ?? []).map((step, stepIndex) => (
                    <li key={stepIndex}>
                      <span>{step?.duration}</span>
                      {step?.label}
                    </li>
                  ))}
                </ol>
              )}
              <ButtonPrimary url={item.ctaHref ?? "/booking"} label={item.ctaLabel ?? ""} />
            </article>
          ))}
        </div>
      </section>

      <section className="kma-story">
        <div>
          <p className="kma-eyebrow" data-tina-field={tinaField(story, "eyebrow")}>
            {story.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(story, "title")}>{story.title}</h2>
        </div>
        <p data-tina-field={tinaField(story, "text")}>{story.text}</p>
      </section>

      <section className="kma-benefits" aria-label="KMA Massage benefits">
        {(benefitCards ?? []).map((benefit, index) => (
          <article key={index} className="kma-benefit" data-tina-field={tinaField(benefit)}>
            {benefit.icon && <Icon icon={benefit.icon} className="kma-benefit-icon" />}
            <h3>{benefit.title}</h3>
            <p>{benefit.text}</p>
          </article>
        ))}
      </section>

      <section className="kma-spine-guide" aria-labelledby="kma-spine-guide-title">
        <div className="kma-spine-guide-heading">
          <p className="kma-eyebrow" data-tina-field={tinaField(spineGuide, "eyebrow")}>
            {spineGuide.eyebrow}
          </p>
          <h2 id="kma-spine-guide-title" data-tina-field={tinaField(spineGuide, "title")}>
            {spineGuide.title}
          </h2>
          <p data-tina-field={tinaField(spineGuide, "description")}>{withLineBreaks(spineGuide.description)}</p>
        </div>
        <div className="kma-spine-guide-layout">
          <figure className="kma-spine-guide-image">
            <button
              type="button"
              className="kma-spine-image-trigger"
              aria-label={spineGuide.imageAlt ? `Enlarge image: ${spineGuide.imageAlt}` : "Enlarge spine guide image"}
              aria-haspopup="dialog"
              onClick={() => spineImageDialog.current?.showModal()}
            >
              <img
                src={spineGuide.imageSrc ?? undefined}
                alt={spineGuide.imageAlt ?? ""}
                data-tina-field={tinaField(spineGuide, "imageSrc")}
              />
              <span className="kma-spine-image-zoom" aria-hidden="true">
                <Icon icon="tabler:zoom-in" />
              </span>
            </button>
          </figure>
          <div className="kma-spine-guide-regions">
            {(spineGuide.regions ?? []).map((region, regionIndex) => (
              <details key={regionIndex} className="kma-spine-region">
                <summary data-tina-field={tinaField(region, "name")}>{region.name}</summary>
                <div className="kma-vertebra-list">
                  {(region.vertebrae ?? []).map((vertebra, vertebraIndex) => (
                    <article key={vertebraIndex} className="kma-vertebra">
                      <h4 data-tina-field={tinaField(vertebra, "level")}>{vertebra.level}</h4>
                      <p>
                        <strong>Function:</strong>{" "}
                        <span data-tina-field={tinaField(vertebra, "areas")}>{vertebra.areas}</span>
                      </p>
                      <p>
                        <strong>Symptoms:</strong>{" "}
                        <span data-tina-field={tinaField(vertebra, "symptoms")}>{vertebra.symptoms}</span>
                      </p>
                    </article>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
        {spineGuide.massageGoal && (
          <p className="kma-spine-guide-goal" data-tina-field={tinaField(spineGuide, "massageGoal")}>
            {spineGuide.massageGoal}
          </p>
        )}
        <dialog
          ref={spineImageDialog}
          className="kma-spine-image-dialog"
          aria-label={spineGuide.imageAlt || "Enlarged spine guide image"}
          onClick={(event) => {
            if (event.target === event.currentTarget) event.currentTarget.close();
          }}
        >
          <button
            type="button"
            className="kma-spine-image-dialog-close"
            aria-label="Close enlarged image"
            onClick={() => spineImageDialog.current?.close()}
          >
            <Icon icon="tabler:x" />
          </button>
          <img src={spineGuide.imageSrc ?? undefined} alt={spineGuide.imageAlt ?? ""} />
        </dialog>
      </section>

      <section className="kma-feature">
        <img
          src={feature.imageSrc ?? undefined}
          alt={feature.imageAlt ?? ""}
          data-tina-field={tinaField(feature, "imageSrc")}
        />
        <div>
          <p className="kma-eyebrow" data-tina-field={tinaField(feature, "eyebrow")}>
            {feature.eyebrow}
          </p>
          <h2 data-tina-field={tinaField(feature, "title")}>{withLineBreaks(feature.title)}</h2>
          <p data-tina-field={tinaField(feature, "text")}>{feature.text}</p>
          <ButtonPrimary url={feature.ctaHref ?? "/booking"} label={feature.ctaLabel ?? ""} />
        </div>
      </section>
    </main>
  );
};

export default MassageServices;
